import * as XLSX from 'xlsx'
import JSZip from 'jszip'
import JSZipUtils from 'jszip-utils'
import { message } from 'antd'
import Compressor from 'compressorjs'

export function saveAs(href: string | Blob, fileName: string) {
  const isBlob = href instanceof Blob
  const aLink = document.createElement('a')
  aLink.href = isBlob ? window.URL.createObjectURL(href) : href
  aLink.download = fileName
  aLink.click()
  if (isBlob) setTimeout(() => URL.revokeObjectURL(aLink.href), 100)
}

// 支持cdn文件下载
export function xhrDownload(
  url: string,
  fileName?: string,
  fileType: string = 'image'
) {
  return new Promise((reslove, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.responseType = 'blob'

    xhr.onload = () => {
      if (xhr.status === 200) {
        const defaultName = url.substring(url.lastIndexOf('/') + 1)
        saveAs(xhr.response, decodeURIComponent(fileName || defaultName))
        reslove(fileName)
      }
    }
    xhr.onerror = err => reject(err)

    xhr.open('get', url, true)

    if (fileType === 'image') {
      // 为了排除webp
      xhr.setRequestHeader('accept', 'image/jpeg, image/png')
      // 不使用缓存
      xhr.setRequestHeader('If-Modified-Since', '0')
    }

    xhr.send()
  })
}

/** 多文件合并成zip压缩包下载 */
export const mutliFilesDownloadToZip = (
  filesUrls: string[],
  zipFileName: string = 'download-zip'
) => {
  const zip = new JSZip()

  const messageKey = 'download-zip'

  return new Promise<void>((reslove, reject) => {
    let count = 0
    message.destroy()

    message.loading({
      content: `开始下载，请耐心等待～`,
      duration: 0,
      key: messageKey,
    })

    filesUrls.forEach(url => {
      const fileName = url.substring(url.lastIndexOf('/') + 1)

      JSZipUtils.getBinaryContent(url, {
        callback: (err: any, data: any) => {
          if (err) {
            message.error({
              content: `下载失败，请重试`,
              duration: 3,
              key: messageKey,
            })
            reject(err)
          }

          zip.file(fileName, data, { binary: true })
          count++

          if (count == filesUrls.length) {
            message.destroy(messageKey)
            zip.generateAsync({ type: 'blob' }).then(content => {
              saveAs(content, zipFileName)
              reslove()
            })
          } else {
            message.loading({
              content: `下载进度：${((count / filesUrls.length) * 100).toFixed(
                2
              )}%`,
              duration: 0,
              key: messageKey,
            })
          }
        },
      })
    })
  })
}

export function trimFileExt(s?: string) {
  return s?.replace(/\.[^/.]+$/, '') || ''
}

// 获取资源的base64
export function getBase64(
  img: File | Blob | undefined,
  callback: (url: string) => void
) {
  if (img) {
    const reader = new FileReader()
    reader.addEventListener('load', () => {
      if (reader.result && typeof reader.result === 'string') {
        callback(reader.result)
      }
    })
    reader.readAsDataURL(img)
  }
}

/** 获取图片宽高 */
export const getImageSizes = (
  img: File | Blob,
  callback: ({ width, height }: { width: number; height: number }) => void
) => {
  const reader = new FileReader()
  reader.addEventListener('load', () => {
    if (reader.result && typeof reader.result === 'string') {
      const image = new Image()
      image.onload = () => {
        callback({
          width: image.width,
          height: image.height,
        })
      }
      image.src = reader.result
    }
  })
  reader.readAsDataURL(img)
}

/**
 * 导入excel
 * @param file 文件
 */
export const importExcelByXLSX = (file: Blob): Promise<any> => {
  const fileReader: FileReader = new FileReader()
  return new Promise((resolve, reject) => {
    fileReader.onload = (e: ProgressEvent<FileReader>) => {
      try {
        const { result } = e.target || {}
        const workbook = XLSX.read(result, {
          type: 'binary',
        })
        let data: any[] = []
        for (const sheet in workbook.Sheets) {
          if (workbook.Sheets.hasOwnProperty(sheet)) {
            // 利用 sheet_to_json 方法将 excel 转成 json 数据
            data = data.concat(XLSX.utils.sheet_to_json(workbook.Sheets[sheet]))
            // break; // 如果只取第一张表，就取消注释这行
          }
        }
        resolve(data)
      } catch (error) {
        console.log('error', error)
        reject(error)
      }
    }
    fileReader.readAsBinaryString(file)
  })
}

export const loadImage = (imageUrl: string) =>
  new Promise<any>((resolve, reject) => {
    const oImg = new Image()
    oImg.crossOrigin = 'anonymous'
    oImg.onload = () => {
      resolve(oImg)
    }
    oImg.onerror = reject
    oImg.src = imageUrl
  })

/** 照片添加ai水印 */
export const drawImageMark = async ({
  imageUrl,
  nickname,
}: {
  [key: string]: any
}) => {
  const canvasNode = document.createElement('canvas')
  const mainImg = await loadImage(imageUrl)
  const logo = await loadImage('/images/plaza/water-mark-v2.png')
  const width = mainImg.naturalWidth
  const height = mainImg.naturalHeight
  canvasNode.width = width
  canvasNode.height = height
  const logoWidth = logo.naturalWidth
  const logoHeight = logo.naturalHeight
  const cvs = canvasNode.getContext('2d')
  if (!cvs) {
    return ''
  }
  const ratio = width / 375
  // 1.绘制图片
  cvs?.drawImage(mainImg, 0, 0, width, height, 0, 0, width, height)
  cvs?.drawImage(
    logo,
    0,
    0,
    logoWidth,
    logoHeight,
    width - (logoWidth / 2 + 12) * ratio,
    height - (logoHeight / 2 + 12 + 12 + 4) * ratio,
    (logoWidth / 2) * ratio,
    (logoHeight / 2) * ratio
  )
  // 2.绘制昵称
  if (nickname) {
    cvs.font = `normal ${
      12 * ratio
    }px 'PingFang SC', 'Hiragino Sans GB', 'Microsoft YaHei', Roboto,'Helvetica Neue', Helvetica, SimSun, sans-serif`
    cvs.textAlign = 'right'
    cvs.fillStyle = '#fff'
    cvs.fillText(`昵称: ${nickname}`, width - 12 * ratio, height - 12 * ratio)
  }

  return canvasNode.toDataURL('image/jpeg', 1)
}

export interface CompressorOptionsProps {
  quality?: number
  maxWidth?: number
  convertSize?: number
}

export const COMPRESSOR_OPTIONS = {
  quality: 0.8,
  maxWidth: 2048,
  convertSize: 1000000, // 1MB
}

/** 压缩图片 */
export const compressorImage = (
  file: File,
  options?: CompressorOptionsProps
) => {
  return new Promise((resolve, reject) => {
    new Compressor(file, {
      quality: options?.quality || COMPRESSOR_OPTIONS.quality,
      maxWidth: options?.maxWidth || COMPRESSOR_OPTIONS.maxWidth,
      convertSize: options?.convertSize || COMPRESSOR_OPTIONS.convertSize, // 超过图片大小限制会转成jpg
      success(result) {
        const newFile = new File([result], result.name, { type: result.type })
        resolve(newFile)
      },
      error(err) {
        reject(err)
        console.log(err && err.message)
      },
    })
  })
}
